import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { API, api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const anonId = `web_${Math.random().toString(36).slice(2, 12)}`;
const GREETING = { role: "assistant", content: "Hi, I'm Sho — AiSho's AI concierge. Ask me about our BPO, automation, LMS or streamer tooling." };

export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loadedFor, setLoadedFor] = useState(null);
  const scrollRef = useRef(null);

  const sessionId = user ? `client_${user.user_id}` : anonId;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  // Load persistent history for logged-in clients when opened
  useEffect(() => {
    if (!open || !user || loadedFor === sessionId) return;
    (async () => {
      try {
        const { data } = await api.get(`/chat/history/${sessionId}`);
        if (data && data.length) {
          setMessages([GREETING, ...data.map((m) => ({ role: m.role, content: m.content }))]);
        }
      } catch {}
      setLoadedFor(sessionId);
    })();
  }, [open, user, sessionId, loadedFor]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I'm unavailable right now. Please use the contact form." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-toggle-btn"
        className="fixed bottom-6 right-6 z-[60] h-14 w-14 rounded-full bg-cyan-400 text-black flex items-center justify-center shadow-[0_0_30px_rgba(0,229,255,0.35)]"
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-window"
            className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-[380px] h-[520px] bg-[#0C0C0C] border border-white/10 rounded-2xl flex flex-col overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2.5">
              <Sparkles size={16} className="text-cyan-400" />
              <div>
                <p className="text-sm text-white font-medium leading-none">Sho</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 mt-1">
                  {user ? "Your Concierge" : "AI Concierge"}
                </p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-cyan-400 text-black rounded-br-sm"
                        : "bg-white/[0.06] text-white/85 rounded-bl-sm border border-white/10"
                    }`}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-white/10 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about our services…"
                data-testid="chat-input"
                className="flex-1 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-400/60 transition-colors"
              />
              <button
                onClick={send}
                disabled={streaming}
                data-testid="chat-send-btn"
                className="h-10 w-10 shrink-0 rounded-full bg-cyan-400 text-black flex items-center justify-center disabled:opacity-40"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
